import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Store, Phone, MapPin, FileText, CheckCircle, Upload, X, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DealerRegistration = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    company_name: '',
    phone: '',
    address: ''
  });
  
  const [documents, setDocuments] = useState({
    dealer_license: null,
    id_card: null,
    cac_certificate: null
  });
  
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = (type, file) => {
    if (file) {
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        setError('Only PDF, JPG, and PNG files are allowed');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }
      setDocuments({ ...documents, [type]: file });
      setError('');
    }
  };

  const uploadDocument = async (type, file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', type);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/dealers/upload-document`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          withCredentials: true
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Upload failed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      navigate('/login');
      return;
    }

    // Validate at least 2 documents
    const selectedDocs = Object.values(documents).filter(doc => doc !== null);
    if (selectedDocs.length < 2) {
      setError('Please upload at least 2 verification documents');
      return;
    }

    setLoading(true);
    setUploading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      // Step 1: Register as dealer
      await axios.post(
        `${API}/dealers/register`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );

      // Step 2: Upload documents
      const uploadPromises = Object.entries(documents)
        .filter(([_, file]) => file !== null)
        .map(([type, file]) => uploadDocument(type, file));

      await Promise.all(uploadPromises);

      setSuccess(true);
      setTimeout(() => {
        navigate('/dealer-dashboard');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Registration failed');
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="glass max-w-md w-full rounded-sm p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-heading font-bold mb-2">Registration Submitted!</h2>
          <p className="text-textSecondary mb-4">
            Your dealer application and documents have been submitted for verification.
            You'll be notified once your account is verified.
          </p>
          <div className="w-full bg-primary/20 rounded-full h-2 overflow-hidden">
            <div className="bg-primary h-full animate-progress"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12 bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="glass rounded-sm p-8">
          <div className="text-center mb-8">
            <Store className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-4xl font-heading font-bold mb-2">Become a Dealer</h1>
            <p className="text-textSecondary">
              Join JAMOH & CO AUTOS LTD as a verified dealer
            </p>
          </div>

          {error && (
            <div className="bg-red-900/20 border border-red-500 rounded-sm p-4 mb-6 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-200">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Company Information */}
            <div>
              <h3 className="text-xl font-heading font-bold mb-4">Company Information</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Company Name *
                  </label>
                  <div className="relative">
                    <Store className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-textSecondary" />
                    <input
                      type="text"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      required
                      className="w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm pl-12 pr-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary"
                      placeholder="Your Company Name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Phone Number *
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-textSecondary" />
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      className="w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm pl-12 pr-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary"
                      placeholder="+234 XXX XXX XXXX"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Business Address *
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-4 top-4 w-5 h-5 text-textSecondary" />
                    <textarea
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      required
                      rows={3}
                      className="w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm pl-12 pr-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary"
                      placeholder="Your business address"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Verification Documents */}
            <div>
              <h3 className="text-xl font-heading font-bold mb-2">Verification Documents</h3>
              <p className="text-sm text-textSecondary mb-4">
                Upload at least 2 documents (PDF, JPG, or PNG - Max 5MB each)
              </p>

              <div className="space-y-4">
                {/* Dealer License */}
                <div className="border border-white/10 rounded-sm p-4">
                  <label className="block text-sm font-medium mb-2">
                    Dealer License
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange('dealer_license', e.target.files[0])}
                    className="hidden"
                    id="dealer_license"
                  />
                  <label
                    htmlFor="dealer_license"
                    className="flex items-center justify-center gap-2 bg-surfaceHighlight border border-white/10 rounded-sm p-4 cursor-pointer hover:border-primary transition-colors"
                  >
                    <Upload className="w-5 h-5" />
                    <span>{documents.dealer_license ? documents.dealer_license.name : 'Choose File'}</span>
                  </label>
                </div>

                {/* ID Card */}
                <div className="border border-white/10 rounded-sm p-4">
                  <label className="block text-sm font-medium mb-2">
                    Dealer ID Card
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange('id_card', e.target.files[0])}
                    className="hidden"
                    id="id_card"
                  />
                  <label
                    htmlFor="id_card"
                    className="flex items-center justify-center gap-2 bg-surfaceHighlight border border-white/10 rounded-sm p-4 cursor-pointer hover:border-primary transition-colors"
                  >
                    <Upload className="w-5 h-5" />
                    <span>{documents.id_card ? documents.id_card.name : 'Choose File'}</span>
                  </label>
                </div>

                {/* CAC Certificate */}
                <div className="border border-white/10 rounded-sm p-4">
                  <label className="block text-sm font-medium mb-2">
                    CAC Certificate
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange('cac_certificate', e.target.files[0])}
                    className="hidden"
                    id="cac_certificate"
                  />
                  <label
                    htmlFor="cac_certificate"
                    className="flex items-center justify-center gap-2 bg-surfaceHighlight border border-white/10 rounded-sm p-4 cursor-pointer hover:border-primary transition-colors"
                  >
                    <Upload className="w-5 h-5" />
                    <span>{documents.cac_certificate ? documents.cac_certificate.name : 'Choose File'}</span>
                  </label>
                </div>
              </div>

              <div className="mt-4 text-sm text-textSecondary">
                Selected: {Object.values(documents).filter(d => d !== null).length}/3 documents
                {Object.values(documents).filter(d => d !== null).length < 2 && (
                  <span className="text-yellow-500 ml-2">(Minimum 2 required)</span>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || uploading}
              className="w-full bg-primary hover:bg-primaryHover text-white rounded-sm font-bold px-6 py-4 transition-colors disabled:opacity-50 disabled:cursor-not-allowed red-glow"
            >
              {uploading ? 'Uploading Documents...' : loading ? 'Processing...' : 'Submit Application'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DealerRegistration;
